// Body Scan Analytics Service
import { supabase } from './supabaseClient';

// Get all body scan sessions with stats
export const getBodyScanSessions = async (days = 30, limit = 50) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    console.log('Fetching body scan sessions...');
    console.log('Days:', days, 'Limit:', limit);
    console.log('Start date:', startDate.toISOString());

    const { data, error } = await supabase
      .from('body_scan_sessions')
      .select('*')
      .gte('started_at', startDate.toISOString())
      .order('started_at', { ascending: false })
      .limit(limit);

    console.log('Sessions data:', data);
    console.log('Sessions error:', error);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching body scan sessions:', error);
    return [];
  }
};

// Get body scan overview stats
export const getBodyScanOverview = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    console.log('Fetching body scan overview...');
    console.log('Start date filter:', startDate.toISOString());

    // First, try to get all sessions without date filter to debug
    const { data: allSessions, error: allError } = await supabase
      .from('body_scan_sessions')
      .select('*');

    console.log('All sessions (no date filter):', allSessions);
    console.log('All sessions error:', allError);

    const { data: sessions, error } = await supabase
      .from('body_scan_sessions')
      .select('*')
      .gte('started_at', startDate.toISOString());

    console.log('Filtered sessions:', sessions);
    console.log('Query error:', error);

    if (error) throw error;

    const completedSessions = (sessions || []).filter(s => s.was_completed);
    const totalSessions = sessions?.length || 0;
    const completionRate = totalSessions > 0
      ? Math.round((completedSessions.length / totalSessions) * 100)
      : 0;

    // Calculate average tension reduction
    let totalReduction = 0;
    let reductionCount = 0;
    completedSessions.forEach(s => {
      if (s.pre_tension_level && s.post_tension_level) {
        totalReduction += (s.pre_tension_level - s.post_tension_level);
        reductionCount++;
      }
    });
    const avgTensionReduction = reductionCount > 0
      ? (totalReduction / reductionCount).toFixed(1)
      : 0;

    // Calculate average duration
    const avgDuration = completedSessions.length > 0
      ? Math.round(completedSessions.reduce((acc, s) => acc + (s.duration_actual || 0), 0) / completedSessions.length)
      : 0;

    // Calculate average rating
    const ratedSessions = completedSessions.filter(s => s.user_rating);
    const avgRating = ratedSessions.length > 0
      ? (ratedSessions.reduce((acc, s) => acc + s.user_rating, 0) / ratedSessions.length).toFixed(1)
      : 0;

    // Get unique users
    const uniqueUsers = new Set((sessions || []).map(s => s.user_id)).size;

    return {
      totalSessions,
      completedSessions: completedSessions.length,
      completionRate,
      avgTensionReduction,
      avgDuration,
      avgRating,
      uniqueUsers
    };
  } catch (error) {
    console.error('Error fetching body scan overview:', error);
    return {
      totalSessions: 0,
      completedSessions: 0,
      completionRate: 0,
      avgTensionReduction: 0,
      avgDuration: 0,
      avgRating: 0,
      uniqueUsers: 0
    };
  }
};

// Get technique effectiveness data
export const getTechniqueEffectiveness = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('technique_zone_effectiveness')
      .select('*')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Group by technique
    const techniqueStats = {};
    (data || []).forEach(row => {
      const id = row.technique_id;
      if (!techniqueStats[id]) {
        techniqueStats[id] = {
          technique_id: id,
          total: 0,
          very_effective: 0,
          effective: 0,
          somewhat_effective: 0,
          not_effective: 0,
          avgReduction: 0,
          totalReduction: 0
        };
      }
      techniqueStats[id].total++;
      techniqueStats[id][row.effectiveness] = (techniqueStats[id][row.effectiveness] || 0) + 1;
      techniqueStats[id].totalReduction += (row.tension_before - row.tension_after);
    });

    // Calculate averages and sort by effectiveness
    const techniques = Object.values(techniqueStats).map(t => ({
      ...t,
      avgReduction: t.total > 0 ? (t.totalReduction / t.total).toFixed(1) : 0,
      effectivenessRate: t.total > 0
        ? Math.round(((t.very_effective + t.effective) / t.total) * 100)
        : 0
    }));

    return techniques.sort((a, b) => b.effectivenessRate - a.effectivenessRate);
  } catch (error) {
    console.error('Error fetching technique effectiveness:', error);
    return [];
  }
};

// Get zone tension data
export const getZoneTensionData = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('technique_zone_effectiveness')
      .select('zone_key, tension_before, tension_after')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Group by zone
    const zoneStats = {};
    (data || []).forEach(row => {
      const zone = row.zone_key;
      if (!zoneStats[zone]) {
        zoneStats[zone] = {
          zone: zone,
          count: 0,
          totalBefore: 0,
          totalAfter: 0
        };
      }
      zoneStats[zone].count++;
      zoneStats[zone].totalBefore += row.tension_before;
      zoneStats[zone].totalAfter += row.tension_after;
    });

    // Calculate averages
    return Object.values(zoneStats).map(z => ({
      zone: z.zone,
      count: z.count,
      avgBefore: z.count > 0 ? (z.totalBefore / z.count).toFixed(1) : 0,
      avgAfter: z.count > 0 ? (z.totalAfter / z.count).toFixed(1) : 0,
      avgReduction: z.count > 0 ? ((z.totalBefore - z.totalAfter) / z.count).toFixed(1) : 0
    })).sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error fetching zone tension data:', error);
    return [];
  }
};

// Get trigger context distribution
export const getTriggerContexts = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('body_scan_sessions')
      .select('trigger_context')
      .gte('started_at', startDate.toISOString());

    if (error) throw error;

    // Count contexts
    const contexts = {};
    (data || []).forEach(row => {
      const ctx = row.trigger_context || 'unknown';
      contexts[ctx] = (contexts[ctx] || 0) + 1;
    });

    return Object.entries(contexts)
      .map(([context, count]) => ({ context, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error fetching trigger contexts:', error);
    return [];
  }
};

// Get sessions by day
export const getSessionsByDay = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('body_scan_sessions')
      .select('started_at, was_completed')
      .gte('started_at', startDate.toISOString());

    if (error) throw error;

    // Group by day
    const dayStats = {};
    (data || []).forEach(row => {
      const day = row.started_at.split('T')[0];
      if (!dayStats[day]) {
        dayStats[day] = { date: day, total: 0, completed: 0 };
      }
      dayStats[day].total++;
      if (row.was_completed) dayStats[day].completed++;
    });

    return Object.values(dayStats).sort((a, b) => a.date.localeCompare(b.date));
  } catch (error) {
    console.error('Error fetching sessions by day:', error);
    return [];
  }
};

// Get favorite techniques stats
export const getFavoriteTechniques = async () => {
  try {
    const { data, error } = await supabase
      .from('user_centering_technique_progress')
      .select('*')
      .eq('is_favorite', true);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching favorite techniques:', error);
    return [];
  }
};

// Get all technique progress with aggregated stats
export const getTechniqueProgressStats = async () => {
  try {
    const { data, error } = await supabase
      .from('user_centering_technique_progress')
      .select('*');

    if (error) throw error;

    // Aggregate stats by technique
    const techniqueStats = {};
    (data || []).forEach(row => {
      const id = row.technique_id;
      if (!techniqueStats[id]) {
        techniqueStats[id] = {
          technique_id: id,
          total_users: 0,
          favorites_count: 0,
          total_completions: 0,
          total_duration: 0,
          total_ratings: 0,
          ratings_sum: 0,
          avg_mastery: 0,
          mastery_sum: 0
        };
      }
      techniqueStats[id].total_users++;
      if (row.is_favorite) techniqueStats[id].favorites_count++;
      techniqueStats[id].total_completions += row.times_completed || 0;
      techniqueStats[id].total_duration += row.total_duration_seconds || 0;
      if (row.avg_rating) {
        techniqueStats[id].ratings_sum += row.avg_rating;
        techniqueStats[id].total_ratings++;
      }
      techniqueStats[id].mastery_sum += row.mastery_level || 0;
    });

    // Calculate averages
    return Object.values(techniqueStats).map(t => ({
      ...t,
      avg_rating: t.total_ratings > 0 ? (t.ratings_sum / t.total_ratings).toFixed(1) : null,
      avg_mastery: t.total_users > 0 ? (t.mastery_sum / t.total_users).toFixed(1) : 0,
      avg_completions: t.total_users > 0 ? (t.total_completions / t.total_users).toFixed(1) : 0
    })).sort((a, b) => b.favorites_count - a.favorites_count);
  } catch (error) {
    console.error('Error fetching technique progress stats:', error);
    return [];
  }
};

// Get top favorited techniques with details
export const getTopFavoriteTechniques = async (limit = 10) => {
  try {
    // Get all progress data
    const { data: progressData, error: progressError } = await supabase
      .from('user_centering_technique_progress')
      .select('*');

    if (progressError) throw progressError;

    // Aggregate by technique
    const techniqueStats = {};
    (progressData || []).forEach(row => {
      const id = row.technique_id;
      if (!techniqueStats[id]) {
        techniqueStats[id] = {
          technique_id: id,
          favorites_count: 0,
          total_users: 0,
          total_completions: 0,
          avg_rating: null,
          ratings_count: 0,
          ratings_sum: 0
        };
      }
      techniqueStats[id].total_users++;
      if (row.is_favorite) techniqueStats[id].favorites_count++;
      techniqueStats[id].total_completions += row.times_completed || 0;
      if (row.avg_rating) {
        techniqueStats[id].ratings_sum += row.avg_rating;
        techniqueStats[id].ratings_count++;
      }
    });

    // Calculate averages and sort by favorites
    const techniques = Object.values(techniqueStats).map(t => ({
      ...t,
      avg_rating: t.ratings_count > 0 ? (t.ratings_sum / t.ratings_count).toFixed(1) : null
    }));

    return techniques
      .sort((a, b) => b.favorites_count - a.favorites_count)
      .slice(0, limit);
  } catch (error) {
    console.error('Error fetching top favorite techniques:', error);
    return [];
  }
};

// Get favorites overview
export const getFavoritesOverview = async () => {
  try {
    const { data, error } = await supabase
      .from('user_centering_technique_progress')
      .select('*');

    if (error) throw error;

    const allProgress = data || [];
    const favorites = allProgress.filter(p => p.is_favorite);
    const uniqueUsers = new Set(allProgress.map(p => p.user_id)).size;
    const usersWithFavorites = new Set(favorites.map(f => f.user_id)).size;

    // Count unique techniques
    const uniqueTechniques = new Set(allProgress.map(p => p.technique_id)).size;
    const favoritedTechniques = new Set(favorites.map(f => f.technique_id)).size;

    return {
      totalFavorites: favorites.length,
      uniqueUsers,
      usersWithFavorites,
      uniqueTechniques,
      favoritedTechniques,
      avgFavoritesPerUser: usersWithFavorites > 0
        ? (favorites.length / usersWithFavorites).toFixed(1)
        : 0
    };
  } catch (error) {
    console.error('Error fetching favorites overview:', error);
    return {
      totalFavorites: 0,
      uniqueUsers: 0,
      usersWithFavorites: 0,
      uniqueTechniques: 0,
      favoritedTechniques: 0,
      avgFavoritesPerUser: 0
    };
  }
};

// =============================================
// USER FEEDBACK FUNCTIONS
// =============================================

// Get all user feedback
export const getUserFeedback = async (days = 30, limit = 50) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('user_feedback')
      .select('*')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user feedback:', error);
    return [];
  }
};

// Get feedback overview stats
export const getFeedbackOverview = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('user_feedback')
      .select('*')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    const feedback = data || [];
    const totalFeedback = feedback.length;
    const unresolvedCount = feedback.filter(f => !f.is_resolved).length;

    // Calculate average rating
    const ratedFeedback = feedback.filter(f => f.rating);
    const avgRating = ratedFeedback.length > 0
      ? (ratedFeedback.reduce((acc, f) => acc + f.rating, 0) / ratedFeedback.length).toFixed(1)
      : 0;

    // Count by type
    const byType = {};
    feedback.forEach(f => {
      byType[f.feedback_type] = (byType[f.feedback_type] || 0) + 1;
    });

    // Count by rating
    const byRating = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratedFeedback.forEach(f => {
      byRating[f.rating] = (byRating[f.rating] || 0) + 1;
    });

    // Positive (4-5) vs Negative (1-2)
    const positiveCount = (byRating[4] || 0) + (byRating[5] || 0);
    const negativeCount = (byRating[1] || 0) + (byRating[2] || 0);

    return {
      totalFeedback,
      unresolvedCount,
      avgRating,
      byType,
      byRating,
      positiveCount,
      negativeCount,
      positivePercentage: ratedFeedback.length > 0
        ? Math.round((positiveCount / ratedFeedback.length) * 100)
        : 0
    };
  } catch (error) {
    console.error('Error fetching feedback overview:', error);
    return {
      totalFeedback: 0,
      unresolvedCount: 0,
      avgRating: 0,
      byType: {},
      byRating: {},
      positiveCount: 0,
      negativeCount: 0,
      positivePercentage: 0
    };
  }
};

// Get feedback by day for chart
export const getFeedbackByDay = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('user_feedback')
      .select('created_at, rating')
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Group by day
    const dayStats = {};
    (data || []).forEach(row => {
      const day = row.created_at.split('T')[0];
      if (!dayStats[day]) {
        dayStats[day] = { date: day, total: 0, positive: 0, negative: 0 };
      }
      dayStats[day].total++;
      if (row.rating >= 4) dayStats[day].positive++;
      if (row.rating <= 2) dayStats[day].negative++;
    });

    return Object.values(dayStats).sort((a, b) => a.date.localeCompare(b.date));
  } catch (error) {
    console.error('Error fetching feedback by day:', error);
    return [];
  }
};

// Mark feedback as resolved
export const resolveFeedback = async (feedbackId, adminNotes = '') => {
  try {
    const { data, error } = await supabase
      .from('user_feedback')
      .update({
        is_resolved: true,
        resolved_at: new Date().toISOString(),
        admin_notes: adminNotes
      })
      .eq('id', feedbackId)
      .select();

    if (error) throw error;
    return data?.[0] || null;
  } catch (error) {
    console.error('Error resolving feedback:', error);
    return null;
  }
};
